/**
 * ErrorState — Reusable error state component (NP-46).
 *
 * Displays an error icon, message, and retry button when
 * a data fetch or operation fails.
 */

import { AlertCircle, type LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { mapErrorToUserMessage } from "@/lib/errors/userMessages";

export interface ErrorStateProps {
  /** Icon to display (defaults to AlertCircle) */
  icon?: LucideIcon;
  /** Error message to show */
  message: string;
  /** Label for the retry button */
  retryLabel?: string;
  /** Retry button handler */
  onRetry?: () => void;
  /** Additional CSS classes */
  className?: string;
}

export function ErrorState({
  icon: Icon = AlertCircle,
  message,
  retryLabel = "Try again",
  onRetry,
  className,
}: ErrorStateProps) {
  const { title: friendlyTitle, message: friendlyMessage } = mapErrorToUserMessage(message);

  return (
    <div className={cn("flex flex-col items-center justify-center py-12 px-4 text-center", className)} role="alert" aria-live="polite">
      <div className="h-14 w-14 rounded-full bg-danger-light/40 grid place-items-center mb-4">
        <Icon className="size-7 text-destructive" />
      </div>
      <h3 className="text-lg font-semibold">{friendlyTitle}</h3>
      <p className="text-sm text-muted-foreground mt-1.5 max-w-md">{friendlyMessage}</p>
      {onRetry && (
        <Button variant="outline" onClick={onRetry} className="mt-4">
          {retryLabel}
        </Button>
      )}
    </div>
  );
}

export default ErrorState;
